import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users, ShoppingBag, Gift, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FoundingSellersCampaign = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    sellers: 0,
    listings: 0,
    spotsLeft: 1000
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/founding-sellers/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching founding seller stats:', error);
      }
    };
    fetchStats();
  }, []);

  const progress = (stats.listings / 1000) * 100;

  return (
    <div className="mx-4 my-4 bg-white border border-stone-200 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="bg-minted-900 px-4 py-3 flex items-center gap-3">
        <div className="p-2 border border-minted-600 rounded-sm">
          <Trophy className="w-5 h-5 text-minted-200" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-display font-medium text-white text-base">Founding Sellers</h3>
          <p className="text-minted-300 text-xs">First 1000 listings earn the badge</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 pt-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="font-medium text-stone-600">{stats.listings} / 1000 listings</span>
          <span className="text-minted-800 font-semibold">{stats.spotsLeft} spots left</span>
        </div>
        <div className="h-1.5 bg-minted-50 rounded-sm overflow-hidden">
          <div 
            className="h-full bg-minted-700 rounded-sm transition-all duration-500"
            style={{ width: `${Math.max(progress, 2)}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 px-4 py-4">
        <div className="text-center">
          <Users className="w-4 h-4 text-minted-600 mx-auto mb-1" strokeWidth={1.5} />
          <p className="text-lg font-display font-medium text-stone-900">{stats.sellers}</p>
          <p className="text-[10px] text-stone-400 tracking-wider uppercase">Sellers</p>
        </div>
        <div className="text-center border-x border-stone-100">
          <ShoppingBag className="w-4 h-4 text-minted-600 mx-auto mb-1" strokeWidth={1.5} />
          <p className="text-lg font-display font-medium text-stone-900">{stats.listings}</p>
          <p className="text-[10px] text-stone-400 tracking-wider uppercase">Listings</p>
        </div>
        <div className="text-center">
          <Gift className="w-4 h-4 text-minted-600 mx-auto mb-1" strokeWidth={1.5} />
          <p className="text-lg font-display font-medium text-stone-900">$1000</p>
          <p className="text-[10px] text-stone-400 tracking-wider uppercase">Prizes</p>
        </div>
      </div>

      {/* Prize Info */}
      <div className="px-4 pb-3">
        <div className="bg-minted-50 rounded-sm p-3 border border-minted-100">
          <p className="font-medium text-minted-900 text-xs mb-0.5">5 Winners &mdash; $200 Each</p>
          <p className="text-[11px] text-minted-700">
            Each listing = 1 entry into the draw. Refer friends = 2 bonus entries.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 pb-4">
        <Button 
          onClick={() => navigate('/sell')}
          className="w-full bg-minted-800 hover:bg-minted-700 text-white rounded-sm py-3 flex items-center justify-center gap-2 text-sm"
        >
          <Trophy className="w-4 h-4" strokeWidth={1.5} />
          List Now & Win $200
          <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
};

export default FoundingSellersCampaign;
